/**
 * @file TermsOfServicePage.jsx
 * @description Official terms of service for SimpleCode. Explains user obligations, 
 *              platform rules, and intellectual property in a clean, Bento-inspired light theme.
 * @module components/TermsOfServicePage
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import {
    FiFileText,
    FiChevronDown,
    FiChevronRight,
    FiArrowRight
} from 'react-icons/fi';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component TermsOfServicePage
 * @description Renders the platform's terms of service with an interactive, categorized layout.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Terms of Service page.
 */
const TermsOfServicePage = ({ navigate }) => {
    const { isAuthenticated } = useAuth();
    // ── Component State ──
    const [expandedTerm, setExpandedTerm] = useState(null);
    const [loading, setLoading] = useState(true);

    /* ══════════════════════════════════════════════════════════════
     * DATA & CONFIG
     * ══════════════════════════════════════════════════════════════ */

    // ── Navigation Links (Synchronized across the platform) ──
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "SimpleCode | Terms of Service";
        // Emulate a light loading state for smooth entry
        const timer = setTimeout(() => setLoading(false), 400);
        return () => {
            clearTimeout(timer);
            document.title = "SimpleCode";
        };
    }, []);

    /**
     * @constant terms
     * @description Complete list of terms and conditions displayed in the accordion.
     * @type {Array<{title: string, content: string}>}
     */
    const terms = [
        {
            title: 'Acceptance of Terms',
            content: 'By accessing and using SimpleCode, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.'
        },
        {
            title: 'Account Registration',
            content: 'You must sign in using Google OAuth to access certain features. You are responsible for maintaining the confidentiality of your account and all activities that occur under it.'
        },
        {
            title: 'Username Policy',
            content: 'You must choose a unique username upon registration. Usernames must not contain offensive, misleading, or inappropriate content. We reserve the right to remove or change usernames that violate this policy.'
        },
        {
            title: 'Acceptable Use',
            content: 'You agree to use SimpleCode only for lawful purposes. You must not attempt to exploit, hack, or disrupt the platform, its services, or other users\' experiences.'
        },
        {
            title: 'Code Submissions',
            content: 'Code you submit is executed in sandboxed environments. You must not submit malicious code, attempt to access system resources beyond what is permitted, or exploit the execution environment.'
        },
        {
            title: 'Rate Limiting',
            content: 'Code execution and submission features are subject to rate limiting. Attempting to bypass rate limits or abuse the execution system may result in temporary or permanent account suspension.'
        },
        {
            title: 'Intellectual Property',
            content: 'All platform content, including problem descriptions, system design materials, tutorials, and educational content, is the intellectual property of SimpleCode and may not be reproduced without permission.'
        },
        {
            title: 'User-Generated Content',
            content: 'You retain ownership of the code you write and submit. By submitting code, you grant SimpleCode a non-exclusive license to store and process it for providing our services.'
        },
        {
            title: 'System Design Content',
            content: 'System design tutorials and interview questions are provided for educational purposes. Redistribution, copying, or commercializing this content without authorization is prohibited.'
        },
        {
            title: 'Batch and Group Learning',
            content: 'If you join a batch or group, you agree to follow the rules set by batch administrators. Your progress data may be shared with batch admins for educational tracking purposes.'
        },
        {
            title: 'Fair Usage Policy',
            content: 'Our platform resources are shared among all users. Excessive or automated usage that may degrade the experience for others is prohibited and may lead to account restrictions.'
        },
        {
            title: 'Privacy',
            content: 'Your use of SimpleCode is also governed by our Privacy Policy. By using our services, you consent to the collection and use of information as described in the Privacy Policy.'
        },
        {
            title: 'Service Availability',
            content: 'We strive to maintain platform availability but do not guarantee uninterrupted access. We may perform maintenance, updates, or experience outages that temporarily affect services.'
        },
        {
            title: 'Modifications to Service',
            content: 'We reserve the right to modify, suspend, or discontinue any feature of the platform at any time. We will make reasonable efforts to notify users of significant changes.'
        },
        {
            title: 'Account Suspension',
            content: 'We may suspend or terminate accounts that violate these terms, engage in abusive behavior, submit malicious code, or otherwise compromise the integrity of our platform.'
        },
        {
            title: 'Disclaimer of Warranties',
            content: 'SimpleCode is provided "as is" without warranties of any kind. We do not guarantee that the platform will be error-free, secure, or continuously available.'
        },
        {
            title: 'Limitation of Liability',
            content: 'SimpleCode shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform, including but not limited to loss of data or profits.'
        },
        {
            title: 'Indemnification',
            content: 'You agree to indemnify and hold SimpleCode harmless from any claims, damages, or expenses arising from your use of the platform or violation of these terms.'
        },
        {
            title: 'Third-Party Services',
            content: 'Our platform integrates with third-party services such as Google OAuth and Supabase. Your use of these services is subject to their respective terms and privacy policies.'
        },
        {
            title: 'Educational Purpose',
            content: 'SimpleCode is an educational platform. The problems, solutions, and materials provided are for learning purposes. We do not guarantee specific career or interview outcomes.'
        },
        {
            title: 'Feedback and Suggestions',
            content: 'Any feedback, suggestions, or ideas you provide may be used by SimpleCode to improve the platform without any obligation or compensation to you.'
        },
        {
            title: 'Prohibited Activities',
            content: 'You must not: share account credentials, use automated tools to scrape content, impersonate others, post spam or inappropriate content, or interfere with platform operations.'
        },
        {
            title: 'Governing Law',
            content: 'These terms are governed by the laws of India. Any disputes arising from these terms shall be resolved in the courts of appropriate jurisdiction in India.'
        },
        {
            title: 'Changes to Terms',
            content: 'We may update these Terms of Service from time to time. Continued use of the platform after changes constitutes acceptance of the updated terms.'
        },
        {
            title: 'Contact',
            content: 'If you have questions about these Terms of Service, please reach out through our contact page or email us at support@simplecode.co.in.'
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-accent flex flex-col font-sans">
                <Navbar navigate={navigate} navLinks={navLinks} />
                <main className="flex-1 pt-16">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 animate-pulse mt-8">
                        <div className="h-10 w-64 bg-emerald-100 rounded mb-8" />
                        <div className="bg-white rounded-lg p-8 shadow-sm space-y-6">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="border-b border-gray-100 pb-4">
                                    <div className="h-6 w-1/3 bg-gray-100 rounded mb-2" />
                                    <div className="h-4 w-full bg-gray-50 rounded" />
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 pt-16">

                {/* ── Terms Header ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mb-8 mt-4 border-b border-emerald-100/30">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h1 className="text-left text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                Terms of <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Service</span>
                            </h1>
                            <p className="text-muted-foreground text-sm font-medium text-left">Last updated: March 2026</p>
                        </div>
                    </div>
                </section>

                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

                        {/* ── Left Side: Sidebar Cards (Sticky on Desktop) ── */}
                        <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start space-y-6 pb-12">
                            <div className="bg-white border-l-4 border-emerald-600 rounded p-6 shadow-sm relative overflow-hidden">
                                <div className="relative z-10">
                                    <h2 className="text-lg font-bold text-emerald-600 mb-3">Our Promise</h2>
                                    <p className="text-muted-foreground text-xs leading-relaxed text-left text-justify">
                                        We are dedicated to providing a fair, secure, and transparent learning environment. 
                                        These terms ensure that SimpleCode remains a valuable resource for all developers.
                                    </p>
                                </div>
                            </div>

                            <div className="flex-col items-center justify-between p-6 bg-emerald-600 rounded text-white shadow-md space-y-2">
                                <div>
                                    <h4 className="text-lg font-bold">Need Help?</h4>
                                    <p className="text-emerald-50 text-xs text-justify my-2">If you have questions about these terms, our support team is ready to assist.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/support')}
                                    className="w-full justify-center bg-white text-emerald-600 px-5 py-2 rounded font-bold text-xs hover:bg-emerald-50 transition-all flex items-center gap-2"
                                >
                                    CONTACT US <FiArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* ── Right Side: Substantive Content (Main Scroll) ── */}
                        <div className="lg:col-span-3">
                            <div className="bg-white border-l-4 border-emerald-600 rounded overflow-hidden relative">

                                {/* Background Watermark */}
                                <div className="hidden md:block absolute right-[-20px] top-[-20px] opacity-[0.15] transform rotate-12 transition-opacity duration-300 pointer-events-none z-20">
                                    <FiFileText className="w-96 h-96" />
                                </div>

                                <div className="relative z-10 divide-y divide-border-border">
                                    {terms.map((term, index) => {
                                        const isExpanded = expandedTerm === index;
                                        return (
                                            <div key={index} className="group transition-colors duration-200 hover:bg-emerald-50/60">
                                                <button
                                                    onClick={() => setExpandedTerm(isExpanded ? null : index)}
                                                    className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
                                                >
                                                    <div className="flex items-center gap-4 hover:text-emerald-600">
                                                        <span className="text-sm font-bold text-emerald-600 group-hover:text-emerald-600 transition-colors w-6">
                                                            {String(index + 1).padStart(2, '0')}
                                                        </span>
                                                        <h3 className={`text-base font-bold transition-colors group-hover:text-emerald-600 ${isExpanded ? 'text-emerald-700' : 'text-foreground'}`}>
                                                            {term.title}
                                                        </h3>
                                                    </div>
                                                    {isExpanded ? (
                                                        <FiChevronDown className="w-5 h-5 text-emerald-600" />
                                                    ) : (
                                                        <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600" />
                                                    )}
                                                </button>

                                                <div
                                                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                                                >
                                                    <div className="overflow-hidden">
                                                        <div className="px-16 pb-6 text-sm text-muted-foreground leading-relaxed text-justify">
                                                            {term.content}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

export default TermsOfServicePage;
